import type { ConfigItem } from '../types';

/**
 * Default configuration items for JSM implementations.
 * These represent the standard configuration areas that need to be addressed
 * in a typical Jira Service Management deployment.
 */
export const DEFAULT_CONFIG_ITEMS: Omit<ConfigItem, 'id'>[] = [
  // ===== PHASE 1: CORE =====
  // Core Structural Items
  {
    name: 'Jira Spaces',
    category: 'field-configuration',
    defaultPhase: 1,
    description: 'Configure Jira Service Management spaces/projects',
    coreFeature: true
  },
  {
    name: 'Teams',
    category: 'team-settings',
    defaultPhase: 1,
    description: 'Configure teams to organize request types',
    coreFeature: true
  },
  {
    name: 'Workflows',
    category: 'automation',
    defaultPhase: 1,
    description: 'Define status workflows for request processing',
    coreFeature: true
  },
  {
    name: 'Work Types',
    category: 'field-configuration',
    defaultPhase: 1,
    description: 'Define work types that map to request types',
    coreFeature: true
  },
  {
    name: 'Request Types',
    category: 'request-type-group',
    defaultPhase: 1,
    description: 'Configure service request types with fields',
    coreFeature: true
  },
  // SLA Configuration
  {
    name: 'SLA Time to First Response',
    category: 'sla',
    defaultPhase: 1,
    description: 'Define SLA goals for initial response time to customer requests',
    coreFeature: true
  },
  {
    name: 'SLA Time to Resolution',
    category: 'sla',
    defaultPhase: 1,
    description: 'Define SLA goals for resolving requests based on priority',
    coreFeature: true
  },
  {
    name: 'SLA Calendars',
    category: 'sla',
    defaultPhase: 1,
    description: 'Configure business hours, time zones, and holiday schedules for SLA calculations',
    coreFeature: true
  },
  {
    name: 'Agent Queues',
    category: 'queue',
    defaultPhase: 1,
    description: 'Create queues for organizing work (Unassigned, My Open Requests, Escalated, etc.)',
    coreFeature: true
  },
  {
    name: 'Queue Filters and JQL',
    category: 'queue',
    defaultPhase: 1,
    description: 'Define filtering logic for each queue using JQL',
    coreFeature: true
  },
  {
    name: 'Basic Approval Workflows',
    category: 'approval',
    defaultPhase: 1,
    description: 'Configure approval processes for requests requiring manager/stakeholder sign-off',
    coreFeature: false
  },
  {
    name: 'Asset Object Schemas',
    category: 'asset',
    defaultPhase: 1,
    description: 'Define asset types (laptops, monitors, software licenses) and their attributes',
    requiresAssets: true,
    defaultAssigneeRole: 'consultant'
  },
  {
    name: 'Asset Import Configuration',
    category: 'asset',
    defaultPhase: 1,
    description: 'Set up automated imports from existing asset management systems',
    requiresAssets: true,
    defaultAssigneeRole: 'consultant'
  },
  {
    name: 'Asset Custom Fields',
    category: 'field-configuration',
    defaultPhase: 1,
    description: 'Configure asset picker fields on request forms',
    requiresAssets: true
  },

  // ===== PHASE 2: AUTOMATION =====
  {
    name: 'Auto-Assignment Rules',
    category: 'automation',
    defaultPhase: 2,
    description: 'Automatically assign requests based on request type, team, or other criteria',
    coreFeature: false
  },
  {
    name: 'Escalation Automation',
    category: 'automation',
    defaultPhase: 2,
    description: 'Auto-escalate requests approaching SLA breach',
    coreFeature: false
  },
  {
    name: 'Status Transition Automation',
    category: 'automation',
    defaultPhase: 2,
    description: 'Automatically transition requests through workflow based on conditions',
    coreFeature: false
  },
  {
    name: 'Email Notifications',
    category: 'notification',
    defaultPhase: 2,
    description: 'Configure when and to whom email notifications are sent',
    coreFeature: true
  },
  {
    name: 'Slack/Teams Integration Notifications',
    category: 'notification',
    defaultPhase: 2,
    description: 'Send notifications to Slack or Microsoft Teams channels',
    coreFeature: false
  },
  {
    name: 'Email Request Channels',
    category: 'email',
    defaultPhase: 2,
    description: 'Configure email addresses that create requests (support@company.com)',
    coreFeature: false
  },
  {
    name: 'Email Templates',
    category: 'email',
    defaultPhase: 2,
    description: 'Customize auto-response and notification email templates',
    coreFeature: false
  },

  // ===== PHASE 3: PORTAL =====
  {
    name: 'Portal Branding & Theme',
    category: 'portal',
    defaultPhase: 3,
    description: 'Customize logo, colors, and CSS for customer portal',
    coreFeature: false
  },
  {
    name: 'Portal Announcements',
    category: 'portal',
    defaultPhase: 3,
    description: 'Configure banners and announcements for planned maintenance or updates',
    coreFeature: false
  },
  {
    name: 'Portal Help Text',
    category: 'portal',
    defaultPhase: 3,
    description: 'Add custom instructions and guidance text throughout the portal',
    coreFeature: false
  },
  {
    name: 'Request Type Categories',
    category: 'request-type-group',
    defaultPhase: 3,
    description: 'Organize request types into logical categories for easier navigation',
    coreFeature: false
  },
  {
    name: 'Request Type Icons',
    category: 'request-type-group',
    defaultPhase: 3,
    description: 'Add visual icons to request types for better user experience',
    coreFeature: false
  },
  {
    name: 'Knowledge Base Setup',
    category: 'knowledge-base',
    defaultPhase: 3,
    description: 'Create and organize help articles and FAQs',
    coreFeature: false
  },
  {
    name: 'Knowledge Base Search Configuration',
    category: 'knowledge-base',
    defaultPhase: 3,
    description: 'Optimize search and article suggestions',
    coreFeature: false
  },
  {
    name: 'Conditional Field Logic',
    category: 'field-configuration',
    defaultPhase: 3,
    description: 'Show/hide fields based on other field values',
    coreFeature: false
  },

  // ===== PHASE 4: ADVANCED =====
  {
    name: 'Slack Integration',
    category: 'integration',
    defaultPhase: 4,
    description: 'Connect Jira Service Management with Slack for bi-directional communication',
    coreFeature: false
  },
  {
    name: 'Microsoft Teams Integration',
    category: 'integration',
    defaultPhase: 4,
    description: 'Connect Jira Service Management with Microsoft Teams',
    coreFeature: false
  },
  {
    name: 'Confluence Knowledge Base Link',
    category: 'integration',
    defaultPhase: 4,
    description: 'Link Confluence spaces for knowledge management',
    coreFeature: false
  },
  {
    name: 'Third-Party App Integrations',
    category: 'integration',
    defaultPhase: 4,
    description: 'Configure connections to external systems (monitoring tools, CMDB, etc.)',
    coreFeature: false
  },
  {
    name: 'CSAT Survey Configuration',
    category: 'csat',
    defaultPhase: 4,
    description: 'Set up customer satisfaction surveys sent after request resolution',
    coreFeature: false
  },
  {
    name: 'CSAT Reporting',
    category: 'csat',
    defaultPhase: 4,
    description: 'Configure dashboards and reports for satisfaction metrics',
    coreFeature: false
  },
  {
    name: 'Performance Dashboards',
    category: 'dashboard',
    defaultPhase: 4,
    description: 'Create dashboards showing team performance, SLA compliance, etc.',
    coreFeature: false
  },
  {
    name: 'Custom Reports',
    category: 'dashboard',
    defaultPhase: 4,
    description: 'Build custom reports for stakeholder visibility',
    coreFeature: false
  },
  {
    name: 'Service Catalog Definition',
    category: 'service-catalog',
    defaultPhase: 4,
    description: 'Document all services offered by the service desk',
    coreFeature: false
  },
  {
    name: 'Team Workload Balancing',
    category: 'team-settings',
    defaultPhase: 4,
    description: 'Configure round-robin or skill-based assignment for balanced workload',
    coreFeature: false
  },
  {
    name: 'Agent Availability Calendars',
    category: 'team-settings',
    defaultPhase: 4,
    description: 'Track agent schedules and time off for accurate workload planning',
    coreFeature: false
  }
];

/**
 * Generate default config items with unique IDs
 */
export function generateDefaultConfigItems(): ConfigItem[] {
  return DEFAULT_CONFIG_ITEMS.map((item, index) => ({
    ...item,
    id: `default-config-${index + 1}-${Date.now()}`
  }));
}
